import { Component } from "@theme/component";

/**
 * A custom element that manages the desktop menu button and the panel it opens.
 *
 * The panel is a disclosure, not a modal: it is a sibling of the button in the
 * DOM, so tabbing out of the button walks straight into it and no focus trap is
 * needed. Escape closes it and returns focus to the button.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The button that opens and closes the panel.
 * @property {HTMLElement} panel - The panel itself.
 * @property {HTMLElement[]} [tab] - The tabs, in the `tabs` layout.
 * @property {HTMLElement[]} [pane] - The panes the tabs activate.
 *
 * @extends {Component<Refs>}
 */
class MenuPanel extends Component {
  requiredRefs = ["trigger", "panel"];

  /**
   * Grace period before a hover-opened panel closes, so a pointer crossing the
   * seam between the button and the panel doesn't snap it shut.
   * @type {number}
   */
  #closeDelay = 200;

  /** @type {number | null} */
  #closeTimeout = null;

  /**
   * Whether the panel was opened by click or keyboard rather than by hover.
   * Only a deliberately opened panel locks the page behind it.
   * @type {boolean}
   */
  #openedDeliberately = false;

  /**
   * Cached on connect: by the time `disconnectedCallback` runs this element is
   * already detached, so `closest` can no longer find the header to clean up.
   * @type {HTMLElement | null}
   */
  #header = null;

  connectedCallback() {
    super.connectedCallback();

    this.#header = /** @type {HTMLElement | null} */ (
      this.closest("header-component")
    );

    document.addEventListener("keydown", this.#onKeyDown);
    document.addEventListener("click", this.#onDocumentClick);
    window.addEventListener("resize", this.#onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener("keydown", this.#onKeyDown);
    document.removeEventListener("click", this.#onDocumentClick);
    window.removeEventListener("resize", this.#onResize);
    this.#clearCloseTimeout();

    // The panel may be torn down mid-open by a section re-render, which would
    // otherwise strand the scroll lock and the header's open state.
    this.#releasePage();
    this.#header?.removeAttribute("data-menu-panel-open");
    this.#header?.style.setProperty("--submenu-height", "0px");
  }

  /**
   * @returns {boolean} Whether the panel is open.
   */
  get isOpen() {
    return this.hasAttribute("open");
  }

  /**
   * @returns {boolean} Whether hovering the button is enough to open the panel.
   */
  get opensOnHover() {
    const mode = this.dataset.trigger;
    return mode === "hover" || mode === "both";
  }

  /**
   * Toggle the panel. Always wired to the button, whatever the trigger mode, so
   * the panel stays reachable by keyboard and by touch when it opens on hover.
   */
  toggle() {
    if (this.isOpen && this.#openedDeliberately) {
      this.close();
    } else {
      this.open(true);
    }
  }

  /**
   * Open the panel.
   * @param {boolean} [deliberate] - True when opened by click or keyboard rather than hover.
   */
  open(deliberate = false) {
    this.#clearCloseTimeout();

    if (this.isOpen) {
      // A hover-opened panel that is then clicked becomes a deliberate one, so
      // it starts locking the page and stops closing when the pointer wanders.
      this.#openedDeliberately ||= deliberate;
      if (this.#openedDeliberately) this.#lockPage();
      return;
    }

    this.#openedDeliberately = deliberate;
    this.setAttribute("open", "");
    this.refs.trigger.setAttribute("aria-expanded", "true");
    this.#header?.setAttribute("data-menu-panel-open", "");

    if (deliberate) this.#lockPage();

    this.#syncSubmenuHeight();
  }

  /**
   * Close the panel.
   * @param {boolean} [restoreFocus] - Whether to move focus back to the button.
   */
  close(restoreFocus = false) {
    this.#clearCloseTimeout();

    if (!this.isOpen) return;

    this.removeAttribute("open");
    this.refs.trigger.setAttribute("aria-expanded", "false");
    this.#header?.removeAttribute("data-menu-panel-open");
    this.#header?.style.setProperty("--submenu-height", "0px");
    this.#openedDeliberately = false;
    this.#releasePage();

    if (restoreFocus) this.refs.trigger.focus();
  }

  /**
   * Open on hover, when the trigger mode asks for it.
   */
  handlePointerEnter = () => {
    this.#clearCloseTimeout();

    if (!this.opensOnHover) return;
    // A coarse pointer fires pointerenter on tap, which would open the panel and
    // let the click that follows close it again.
    if (!window.matchMedia("(hover: hover)").matches) return;

    this.open();
  };

  /**
   * Close on pointer-out, unless the panel was opened deliberately.
   */
  handlePointerLeave = () => {
    if (!this.isOpen || this.#openedDeliberately) return;

    this.#clearCloseTimeout();
    this.#closeTimeout = setTimeout(() => this.close(), this.#closeDelay);
  };

  /**
   * @returns {boolean} Whether tabs change on click rather than on hover.
   */
  get tabsChangeOnClick() {
    return this.dataset.tabsChangeOn === "click";
  }

  /**
   * Show the pane belonging to the hovered or focused tab.
   * @param {PointerEvent | FocusEvent} event
   */
  activatePane(event) {
    // In click mode the pointer only previews a tab; nothing switches until the
    // tab is clicked. Focus follows the same rule so the keyboard matches.
    if (this.tabsChangeOnClick) return;

    this.#showPane(event.target);
  }

  /**
   * In click mode, clicking a tab switches to its pane. Tabs with a pane are
   * buttons, so there is no link to follow; the pane heading carries it.
   * @param {MouseEvent} event
   */
  handleTabClick(event) {
    if (!this.tabsChangeOnClick) return;

    const tab = event.target;
    if (!(tab instanceof HTMLElement) || !tab.dataset.paneTarget) return;
    if (tab.hasAttribute("data-active")) return;

    event.preventDefault();
    this.#showPane(tab);
  }

  /**
   * @param {EventTarget | null} target - The tab to show the pane for.
   */
  #showPane(target) {
    if (!(target instanceof HTMLElement)) return;

    const paneTarget = target.dataset.paneTarget;
    // Tabs without children leave the current pane in place rather than
    // blanking the panel out.
    if (!paneTarget) return;

    for (const tab of this.refs.tab ?? []) {
      tab.toggleAttribute("data-active", tab === target);
    }

    for (const pane of this.refs.pane ?? []) {
      pane.toggleAttribute(
        "data-active",
        pane.dataset.paneIndex === paneTarget,
      );
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== "Escape" || !this.isOpen) return;

    event.stopPropagation();
    this.close(true);
  };

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.isOpen) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.close();
  };

  #onResize = () => {
    if (!this.isOpen) return;

    // Below the desktop breakpoint the drawer takes over and the panel's own
    // ancestor is display:none, which would leave the page locked behind a
    // panel nobody can see.
    if (getComputedStyle(this).display === "none") {
      this.close();
      return;
    }

    this.#syncSubmenuHeight();
  };

  /**
   * Publish the panel's height to the header, which paints the header row and
   * the panel as one surface stretching down by `--submenu-height`. Also
   * publishes where the header row ends, which a fullscreen panel starts its
   * content below: lower while the announcement bar above it is in view.
   */
  #syncSubmenuHeight() {
    const header = this.#header;
    if (!header) return;

    const row = this.closest(".header__row");
    if (row) {
      const bottom = Math.max(row.getBoundingClientRect().bottom, 0);
      this.refs.panel.style.setProperty(
        "--menu-panel-header-bottom",
        `${Math.round(bottom)}px`,
      );
    }

    requestAnimationFrame(() => {
      header.style.setProperty(
        "--submenu-height",
        `${this.refs.panel.offsetHeight}px`,
      );
    });
  }

  #lockPage() {
    document.documentElement.setAttribute("scroll-lock", "");
  }

  #releasePage() {
    // Another overlay may hold the lock, so only give it up if no open dialog or
    // drawer still needs it.
    if (document.querySelector("details[scroll-lock][open], dialog[open]"))
      return;

    document.documentElement.removeAttribute("scroll-lock");
  }

  #clearCloseTimeout() {
    if (this.#closeTimeout === null) return;

    clearTimeout(this.#closeTimeout);
    this.#closeTimeout = null;
  }
}

if (!customElements.get("menu-panel")) {
  customElements.define("menu-panel", MenuPanel);
}
